import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';

import {
  arrow,
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useDismiss,
  useRole,
  useClick,
  useInteractions,
  useTransitionStyles,
  FloatingArrow,
  FloatingFocusManager,
  useId
} from '@floating-ui/react';

import Copy from '@components/atoms/copy/copy';

const WishDialogHint = ({ text = '', hintCta }) => {
  const arrowRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context, middlewareData } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'top',
    middleware: [
      offset({ mainAxis: 20 }),
      flip({ fallbackAxisSideDirection: 'none' }),
      shift({ padding: 15 }),
      arrow({ element: arrowRef })
    ],
    whileElementsMounted: autoUpdate
  });

  const ARROW_WIDTH = 49;
  const ARROW_HEIGHT = 18; // Size of the arrow inside the square..
  const arrowX = middlewareData.arrow?.x ?? 0;
  const transformX = arrowX + ARROW_WIDTH / 2;

  const { isMounted, styles } = useTransitionStyles(context, {
    initial: {
      transform: 'scale(0)'
    },
    open: {
      transform: 'scale(1)'
    },
    common: {
      transformOrigin: `${transformX}px calc(100% + ${ARROW_HEIGHT}px)`
    }
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  const headingId = useId();

  return (
    <>
      <div className="wishDialog__hint-trigger">
        <Copy tag="span" extraSmall dangerouslySetInnerHTML={{ __html: `*${hintCta}` }} />
        <button
          type="button"
          aria-label="Toggle Hint"
          ref={refs.setReference}
          {...getReferenceProps()}
        >
          <Copy smallThick noMargin>
            ?
          </Copy>
        </button>
      </div>
      {isMounted && (
        <FloatingFocusManager context={context} modal={false}>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            aria-labelledby={headingId}
            {...getFloatingProps()}
          >
            <div className="wishDialog__hint-panel" style={styles}>
              <div className="wishDialog__hint-content">
                <Copy
                  id={headingId}
                  smallThick
                  noMargin
                  dangerouslySetInnerHTML={{ __html: text }}
                />
              </div>
            </div>
            <FloatingArrow
              ref={arrowRef}
              context={context}
              stroke="#b2b2b2"
              strokeWidth={2}
              width={49}
              style={{ transform: 'translateY(2px)' }}
              d="M46.5703 49C42.7596 48.8615 38.7631 42.3869 36.1403 38.1377C35.6733 37.3811 35.2498 36.6951 34.8787 36.128C30.3753 29.2456 18.8287 29.1566 14.2506 36.0026C13.8395 36.6173 13.3667 37.3758 12.8435 38.215C10.1782 42.4906 6.20592 48.8626 2.43422 49L46.5703 49Z"
            />
          </div>
        </FloatingFocusManager>
      )}
    </>
  );
};

WishDialogHint.propTypes = {
  hintCta: PropTypes.string.isRequired,
  text: PropTypes.string,
  isModal: PropTypes.bool
};

export default WishDialogHint;
